import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Input from '../components/Input.tsx';
import TopBar from '../components/TopBar';
import { useAccount } from 'wagmi';
import rot13 from '../utils/encode.ts'
import { BASE_URL } from '../utils/constants.ts';
import Footer from '../components/Footer';

const Referral = () => {
  const { address } = useAccount();
  const referralLink = address ? `${BASE_URL}/?ref=${rot13(address)}` : `${BASE_URL}/?ref=`;
  const [copied, setCopied] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqItems = [
    {
      question: 'How do I use my referral link?',
      answer: 'Share your referral link and enjoy our platform with your friends - when they link their wallet through your referral link, you will automatically be registered as a referrer.',
    },
    {
      question: 'When do I receive points?',
      answer: 'Points are awarded when your invited friend connects to your wallet via a referral link.',
    },
  ];

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--page-bg)' }}>
      <div className="flex-grow">
      <TopBar />

      <div className="max-w-7xl mx-auto pt-28 pb-16 px-4 sm:px-6">
        {/* Banner Section */}
        <div className="relative rounded-xl overflow-hidden mb-8 shadow-xl">
          <div className="absolute inset-0" style={{ background: 'var(--primary-gradient)', opacity: 0.8 }}></div>
          <div className="relative z-10 flex flex-col items-center justify-center h-64 sm:h-80 text-center px-4">
            <h1
              className="text-3xl sm:text-5xl font-bold mb-4"
              style={{ color: 'var(--text-primary)', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
            >
              Referral Program
            </h1>
            <p
              className="text-lg sm:text-xl max-w-2xl"
              style={{ color: 'var(--text-primary)' }}
            >
              Invite friends and earn points together!
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
          <Link
            to="/"
            className="px-6 py-3 text-center rounded-lg font-bold transition-all shadow-lg hover:shadow-xl"
            style={{
              background: 'var(--button-bg)',
              color: 'var(--button-text)',
              minWidth: '200px'
            }}
          >
            Start Trading
          </Link>
          <Link
            to="/create"
            className="px-6 py-3 text-center rounded-lg font-bold transition-all shadow-lg hover:shadow-xl"
            style={{
              background: 'var(--button-bg)',
              color: 'var(--button-text)',
              minWidth: '200px'
            }}
          >
            Create a Coin
          </Link>
        </div>

        {/* Referral Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {/* Referral Link Card */}
          <div
            className="p-6 rounded-xl"
            style={{
              background: 'var(--card-bg)',
              border: '2px solid transparent',
              backgroundImage: 'linear-gradient(var(--card-bg), var(--card-bg)), var(--card-border)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              boxShadow: 'var(--shadow)'
            }}
          >
            <div className="flex items-center mb-4">
              <PersonIcon className="mr-2" />
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Your Referral Link
              </h2>
            </div>
            <div className="flex">
              <Input
                value={copied ? 'Copied!' : referralLink}
                type="text"
                placeholder="Your referral link will appear here"
                readOnly={true}
              />
              <button
                onClick={handleCopy}
                className="ml-2 p-3 rounded-lg flex items-center justify-center transition-all"
                style={{
                  background: 'var(--button-secondary-bg)',
                  border: '2px solid var(--button-secondary-border)',
                  color: 'var(--button-secondary-text)',
                  minWidth: '44px'
                }}
                title="Copy to clipboard"
                aria-label="Copy referral link"
              >
                <ClipboardIcon />
              </button>
            </div>
          </div>

          {/* Rewards Card */}
          <div
            className="p-6 rounded-xl"
            style={{
              background: 'var(--card-bg)',
              border: '2px solid transparent',
              backgroundImage: 'linear-gradient(var(--card-bg), var(--card-bg)), var(--card-border)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              boxShadow: 'var(--shadow)'
            }}
          >
            <div className="flex items-center mb-4">
              <GiftIcon className="mr-2" />
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Point Reward Information
              </h2>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start">
                <div className="mt-1 mr-2" style={{ color: 'var(--text-accent)' }}>•</div>
                <span style={{ color: 'var(--text-primary)' }}>
                  Earn points when your referee trades
                </span>
              </li>
              <li className="flex items-start">
                <div className="mt-1 mr-2" style={{ color: 'var(--text-accent)' }}>•</div>
                <span style={{ color: 'var(--text-primary)' }}>
                  Earn points when your referee launches a token
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="rounded-t-xl overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-5 flex justify-between items-center text-left rounded-t-xl"
                  style={{
                    background: 'var(--card-bg)',
                    border: '2px solid transparent',
                    backgroundImage: 'linear-gradient(var(--card-bg), var(--card-bg)), var(--card-border)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                  }}
                >
                  <span className="font-medium text-lg" style={{ color: 'var(--text-primary)' }}>
                    {item.question}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className={`transition-transform duration-200 ${openIndex === index ? 'rotate-180' : ''}`}
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>

                {openIndex === index && (
                  <div
                    className="p-5"
                    style={{
                      background: 'var(--section-bg)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <p>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

// Icon Components
const PersonIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    width="24"
    height="24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const GiftIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    width="24"
    height="24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v13m0-13V6a2 2 0 11-4 0c0 1.1.9 2 2 2h4a2 2 0 100-4h-2m-4 8h8m-8 0h8m-8 4h8"
    />
  </svg>
);

const ClipboardIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    width="20"
    height="20"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-2m-2-2h-4m0 0h4m-4 0v2h4V3m-4 6h4"
    />
  </svg>
);

export default Referral;
